'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { ReactNode, RefObject, useEffect, useState } from 'react';
import AllWorldsCards from '@/app/[locale]/(worlds)/components/AllWorldsCards';
import { GradientMask, ScrollArrow } from '@/components/ScrollEdge';
import ViverseSkeleton from '@/components/ViverseSkeleton';
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton';
import { SWIMLANE_DEFAULT_CONTENT_INSET_CLASS } from '@/constants/swimlane';
import { useScrollable } from '@/hooks/useScrollable';
import { cn } from '@/lib/utils';
import type { WorldCardInfo, WorldCardLane } from '@/types/viverse-world-card';
type SwimlaneProps = {
  title?: ReactNode;
  titleClassName?: string;
  titleAction?: ReactNode;
  description?: ReactNode;
  cardVariant?: 'action' | 'viverse';
  world?: WorldCardLane;
  rooms?: WorldCardInfo[];
  renderContent?: (scrollRef: RefObject<HTMLDivElement | null>, enableDraggingScroll: boolean, contentInsetClassName: string) => ReactNode;
  className?: string;
  scrollContainerClassName?: string;
  contentInsetClassName?: string;
  cardClassName?: string;
  ranking?: boolean;
  hideLiked?: boolean;
  hidePremiumBadge?: boolean;
  forceShowPremiumBadge?: boolean;
  suppressFreshnessBadges?: boolean;
  isExclusive?: boolean;
  rawCardType?: boolean;
  dataRoomIdPrefix?: string;
  scrollClassName?: string;
  lastCardClassName?: string;
  'data-section'?: string;
  'data-component'?: string;
  captureAttributes?: Record<`data-ph-capture-attribute-${string}`, string>;
  variant?: 'default' | 'contained';
  collapsible?: boolean;
  collapseLabel?: string;
  expandLabel?: string;
};
type SwimlaneSkeletonProps = {
  className?: string;
  children?: ReactNode;
  showTitleAction?: boolean;
  scrollContainerClassName?: string;
  variant?: 'default' | 'contained';
};
const variantStyles = {
  default: {
    section: 'gap-1 px-4 md:px-4 lg:gap-[10px] lg:px-6',
    header: 'md:py-2',
    title: 'subheader-3 lg:subheader-2 font-semibold lg:font-semibold',
    scrollContainer: '-mx-4 md:-mx-4 lg:-mx-6',
    contentInset: SWIMLANE_DEFAULT_CONTENT_INSET_CLASS,
    card: 'lg:max-w-[240px] lg:min-w-[240px] p-0 last:!mr-4 md:last:!mr-4 lg:last:!mr-6',
    scrollArrow: 'default',
    gradientMask: true,
    skeletonTitle: 'h-6 w-40 md:h-8 md:w-48'
  },
  contained: {
    section: 'gap-2 rounded-xl border border-white/10 bg-white/5 py-3 md:py-4',
    header: 'px-4 md:px-5',
    title: 'subheader-3 lg:subheader-2 font-semibold lg:font-semibold',
    scrollContainer: '',
    contentInset: '',
    card: 'lg:max-w-[240px] lg:min-w-[240px] p-0',
    scrollArrow: 'flush',
    gradientMask: false,
    skeletonTitle: 'h-5 w-32 lg:h-6 lg:w-40'
  }
} as const;
export default function Swimlane({
  title,
  titleClassName,
  titleAction,
  description,
  cardVariant,
  world,
  rooms,
  renderContent,
  className,
  scrollContainerClassName: scrollContainerClassNameProp,
  contentInsetClassName: contentInsetClassNameProp,
  cardClassName: cardClassNameProp,
  ranking,
  hideLiked,
  hidePremiumBadge,
  forceShowPremiumBadge,
  suppressFreshnessBadges,
  isExclusive,
  rawCardType,
  dataRoomIdPrefix,
  scrollClassName,
  lastCardClassName,
  'data-section': dataSection,
  'data-component': dataComponent,
  captureAttributes,
  variant = 'default',
  collapsible,
  collapseLabel,
  expandLabel
}: SwimlaneProps) {
  const {
    containerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight
  } = useScrollable();
  const [isHovered, setIsHovered] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // CONNECT-5738: hide the right arrow/gradient once the last card is fully in view. useScrollable's
  // affordance is scroll-distance based, so it stays true across the last card's trailing margin —
  // leaving the arrow overlapping (and stealing clicks from) an already-visible last card. Observing
  // the last child's border-box (IntersectionObserver, no layout read) ignores that margin and closes
  // the dead zone. In paginated rails the last child is a load-more sentinel while more pages load —
  // but the trailing margin isn't applied in that state, so there's no dead zone and this degrades to
  // the scroll-distance affordance (harmless).
  const [isEndCardVisible, setIsEndCardVisible] = useState(false);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let observed: Element | null = null;
    const observer = new IntersectionObserver(entries => setIsEndCardVisible(entries[entries.length - 1].intersectionRatio >= 0.99), {
      root: container,
      threshold: [0, 0.99, 1]
    });

    // Re-point at the current last card whenever content changes (e.g. paginated rails stream in).
    const retarget = () => {
      const last = container.lastElementChild;
      if (last === observed) return;
      if (observed) observer.unobserve(observed);
      observed = last;
      if (!last) {
        setIsEndCardVisible(false);
        return;
      }
      observer.observe(last);
    };
    retarget();
    const mutationObserver = new MutationObserver(retarget);
    mutationObserver.observe(container, {
      childList: true
    });
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef]);
  const styles = variantStyles[variant];
  const scrollContainerClassName = scrollContainerClassNameProp ?? styles.scrollContainer;
  const contentInsetClassName = cn(styles.contentInset, contentInsetClassNameProp);
  const cardClassName = cardClassNameProp ?? styles.card;
  return <section {...captureAttributes} data-section={dataSection} data-component={dataComponent} className={cn('relative flex flex-col', styles.section, className)}>
      {(title || titleAction || collapsible) && <div className={cn('flex flex-1 items-center justify-between', styles.header)}>
          {title && <div className={cn(styles.title, titleClassName)}>{title}</div>}
          <div className="flex items-center gap-2">
            {titleAction}
            {collapsible && <button type="button" aria-expanded={!isCollapsed} className="body-2 flex cursor-pointer items-center gap-1 text-white/60 transition-colors hover:text-white/80" onClick={() => setIsCollapsed(prev => !prev)}>
                {isCollapsed ? expandLabel : collapseLabel}
                {isCollapsed ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
              </button>}
          </div>
        </div>}

      {description}

      <div className={cn('relative', scrollContainerClassName, isCollapsed && 'hidden')} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {renderContent ? renderContent(containerRef, canScrollLeft || canScrollRight, contentInsetClassName) : world || rooms ? <AllWorldsCards cardVariant={cardVariant ?? 'viverse'} scrollRef={containerRef} world={world} rooms={rooms} enableDraggingScroll={canScrollLeft || canScrollRight} cardClassName={cardClassName} ranking={ranking} hideLiked={hideLiked} hidePremiumBadge={hidePremiumBadge} forceShowPremiumBadge={forceShowPremiumBadge} suppressFreshnessBadges={suppressFreshnessBadges} isExclusive={isExclusive} rawCardType={rawCardType} contentInsetClassName={contentInsetClassName} dataRoomIdPrefix={dataRoomIdPrefix ?? 'ws'} scrollClassName={scrollClassName ?? 'no-scrollbar flex w-full flex-1 gap-4 overflow-x-auto select-none'} lastCardClassName={lastCardClassName} /> : null}

        {styles.gradientMask && <GradientMask side="right" visible={canScrollRight && !isEndCardVisible} />}
        {styles.gradientMask && <GradientMask side="left" visible={canScrollLeft} />}

        <ScrollArrow side="left" visible={isHovered && canScrollLeft} onClick={scrollLeft} icon={<ChevronLeft className="size-8 stroke-[#121216]" />} variant={styles.scrollArrow} />
        <ScrollArrow side="right" visible={isHovered && canScrollRight && !isEndCardVisible} onClick={scrollRight} icon={<ChevronRight className="size-8 stroke-[#121216]" />} variant={styles.scrollArrow} />
      </div>
    </section>;
}
export function SwimlaneSkeleton(t0) {
  const $ = _c(24);
  const {
    className,
    children,
    showTitleAction,
    scrollContainerClassName: scrollContainerClassNameProp,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const styles = variantStyles[variant];
  const scrollContainerClassName = scrollContainerClassNameProp ?? styles.scrollContainer;
  let t2;
  if ($[0] !== className || $[1] !== styles.section) {
    t2 = cn("relative flex flex-col", styles.section, className);
    $[0] = className;
    $[1] = styles.section;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== styles.header) {
    t3 = cn("flex flex-1 items-center justify-between", styles.header);
    $[3] = styles.header;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== styles.skeletonTitle) {
    t4 = <ViverseSkeleton className={styles.skeletonTitle} />;
    $[5] = styles.skeletonTitle;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== showTitleAction) {
    t5 = showTitleAction && <ViverseSkeleton className="h-5 w-20" />;
    $[7] = showTitleAction;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== scrollContainerClassName) {
    t7 = cn("relative", scrollContainerClassName);
    $[13] = scrollContainerClassName;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== children) {
    t8 = children || <div className={cn("no-scrollbar relative flex w-full flex-1 gap-4 overflow-x-auto max-md:min-h-60 md:min-h-56", SWIMLANE_DEFAULT_CONTENT_INSET_CLASS)}>{Array.from({
        length: 5
      }).map(_temp)}</div>;
    $[15] = children;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t2 || $[21] !== t6 || $[22] !== t9) {
    t10 = <section className={t2}>{t6}{t9}</section>;
    $[20] = t2;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp(_, index) {
  return <ViverseWorldCardSkeleton key={`skeleton-${index}`} className="h-auto flex-none p-0 max-md:max-w-[306px] max-md:min-w-[306px] md:max-w-60 md:min-w-60" />;
}